import { useCallback } from 'react'
import { useAuthStore, selectIsAuthenticated, selectUserEmail } from './authStore'
import { getSupabaseClient } from '@/repository/supabase/client'
import { markIntentionalSignOut } from './sessionFlags'
import { mapAuthError } from './authErrors'

/**
 * Facade hook for components that need to read auth state or trigger
 * sign-in / sign-out. Mirrors the same pattern the rest of the app uses
 * (`useDebts`, `useLedger`) — components never reach into the store
 * directly so the seam is replaceable.
 *
 * The hook degrades gracefully when Supabase isn't configured: calling
 * `signIn()` in local-only mode sets a clear error message instead of
 * silently failing.
 */
export interface UseAuthResult {
  isAuthenticated: boolean
  isLoading: boolean
  email: string | null
  workspaceId: string | null
  signInError: string | null
  magicLinkSent: boolean
  /**
   * v0.8.46 — Error from the workspace resolution retry chain. Null
   * when not yet attempted, when resolution succeeded, or after a
   * `retryWorkspaceResolve` clear. `<CloudSyncError>` reads this.
   */
  workspaceResolveError: string | null
  /**
   * v0.8.46 — Re-run `resolveAndCacheWorkspaceId` + `bindReposToSession`
   * after the user taps the retry button on the error banner.
   * No-op for unauthenticated sessions.
   */
  retryWorkspaceResolve: () => Promise<void>
  signIn: (email: string) => Promise<void>
  /**
   * v0.8.29 — verify a 6-digit OTP code from the magic-link email as an
   * alternative to clicking the link. Codes are paste-only and immune to
   * link-scanning prefetches (Outlook Safe Links, corporate ATP) that
   * consume PKCE codes from clickable links before the user's click. On
   * success the AuthProvider's SIGNED_IN listener fires the same way it
   * would for a successful magic-link redirect.
   */
  verifyOtpCode: (email: string, token: string) => Promise<void>
  signOut: () => Promise<void>
  clearError: () => void
}

export function useAuth(): UseAuthResult {
  const isAuthenticated = useAuthStore(selectIsAuthenticated)
  const isLoading = useAuthStore((s) => s.isLoading)
  const email = useAuthStore(selectUserEmail)
  const workspaceId = useAuthStore((s) => s.currentWorkspaceId)
  const signInError = useAuthStore((s) => s.signInError)
  const magicLinkSent = useAuthStore((s) => s.magicLinkSent)
  const workspaceResolveError = useAuthStore((s) => s.workspaceResolveError)

  const setSignInError = useAuthStore((s) => s.setSignInError)
  const setMagicLinkSent = useAuthStore((s) => s.setMagicLinkSent)
  const setCurrentWorkspaceId = useAuthStore((s) => s.setCurrentWorkspaceId)
  const setWorkspaceResolveError = useAuthStore((s) => s.setWorkspaceResolveError)
  const setWorkspaceResolveAttempted = useAuthStore((s) => s.setWorkspaceResolveAttempted)

  const signIn = useCallback(
    async (emailInput: string) => {
      setSignInError(null)
      setMagicLinkSent(false)

      const trimmed = emailInput.trim()
      if (!trimmed) {
        setSignInError('Enter your email address')
        return
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
        setSignInError('That doesn’t look like a valid email')
        return
      }

      const client = getSupabaseClient()
      if (!client) {
        setSignInError(
          'Cloud sync is not configured for this build. Sign-in is only available on the hosted deployment.'
        )
        return
      }

      const redirectTo =
        typeof window !== 'undefined' ? `${window.location.origin}/auth/callback` : undefined

      const { error } = await client.auth.signInWithOtp({
        email: trimmed,
        options: redirectTo ? { emailRedirectTo: redirectTo } : undefined,
      })

      if (error) {
        const message = mapAuthError(error.message)
        setSignInError(message)
        return
      }

      setMagicLinkSent(true)
    },
    [setSignInError, setMagicLinkSent]
  )

  /**
   * Translates raw Supabase auth error strings into user-actionable copy.
   *
   * Supabase returns plain English error messages that are often technically
   * accurate but unhelpful in context. This function maps the categories we
   * see repeatedly into messages that tell the user what to do next:
   *
   * - **Allowlist / signup-disabled** — invite-only projects use this for
   *   both "email not invited" and "email invited but not yet confirmed."
   *   API doesn't differentiate, so the message covers both cases.
   * - **Rate limit** — free-tier Supabase caps email sends to ~3-4/hour
   *   per email and ~30/hour project-wide. Easy to hit during debugging.
   *   Surfacing this clearly avoids users assuming the app is broken when
   *   it's actually just the email throttle.
   * - **Anything else** — pass the raw message through. We'd rather the
   *   user see Supabase's exact text than a generic "something went wrong."
   */
  // (Function lives outside the hook below so it stays referentially stable
  //  across renders and doesn't need to be in `useCallback`'s dep array.)

  const verifyOtpCode = useCallback(
    async (emailInput: string, token: string) => {
      setSignInError(null)

      const trimmedEmail = emailInput.trim()
      const trimmedToken = token.trim()

      if (!trimmedEmail) {
        setSignInError('Email is missing — request a fresh code from the dialog.')
        return
      }
      // v0.8.30 — Supabase OTP length is configurable per project (default 6;
      // some projects use 8 or other lengths up to 10). Accept any 6–10 digit
      // numeric code and let Supabase decide if it's valid — pre-fix this
      // rejected 8-digit codes locally before they ever hit the server.
      if (!/^\d{6,10}$/.test(trimmedToken)) {
        setSignInError('Enter the numeric code from the email (typically 6 or 8 digits).')
        return
      }

      const client = getSupabaseClient()
      if (!client) {
        setSignInError(
          'Cloud sync is not configured for this build. Sign-in is only available on the hosted deployment.'
        )
        return
      }

      // `verifyOtp({ type: 'email' })` is the paste-the-code counterpart to
      // clicking the magic link. The token is sent by Supabase in the SAME
      // email as the magic link (when the email template includes
      // `{{ .Token }}`) and is immune to link-scanning prefetches — Outlook
      // Safe Links / corporate ATP / Gmail safety scans can't consume a
      // 6-digit code from the email body the way they consume one-shot
      // links.
      const { error } = await client.auth.verifyOtp({
        email: trimmedEmail,
        token: trimmedToken,
        type: 'email',
      })

      if (error) {
        setSignInError(mapAuthError(error.message))
        return
      }
      // Success path: AuthProvider's `onAuthStateChange` SIGNED_IN listener
      // catches the new session and the SignInDialog auto-closes (its
      // existing `isAuthenticated` watcher handles that).
    },
    [setSignInError]
  )

  const signOut = useCallback(async () => {
    const client = getSupabaseClient()
    if (!client) return
    // Mark this sign-out as intentional BEFORE calling Supabase so
    // AuthProvider's onAuthStateChange listener can distinguish it from
    // a refresh-failure SIGNED_OUT and suppress the "Session expired"
    // banner. See markIntentionalSignOut in AuthProvider for the
    // companion sessionStorage flag.
    markIntentionalSignOut()
    // The session-change listener in AuthProvider catches this and clears
    // the store + rebinds repos to local. We don't manually clear state
    // here to keep a single source of truth for transitions.
    await client.auth.signOut()
  }, [])

  const clearError = useCallback(() => {
    setSignInError(null)
    setMagicLinkSent(false)
  }, [setSignInError, setMagicLinkSent])

  const retryWorkspaceResolve = useCallback(async () => {
    const session = useAuthStore.getState().session
    if (!session) return
    // Clear the prior error and attempted flag so the retry chain has
    // a clean slate, then re-run resolution + repo bind. Imported
    // lazily to keep the cycle clean (AuthProvider also imports useAuth
    // indirectly through useAuthStore selectors elsewhere).
    setWorkspaceResolveError(null)
    setWorkspaceResolveAttempted(false)
    const { resolveAndRebind } = await import('./resolveAndRebind')
    await resolveAndRebind(
      session,
      setCurrentWorkspaceId,
      setWorkspaceResolveError,
      setWorkspaceResolveAttempted
    )
  }, [setCurrentWorkspaceId, setWorkspaceResolveError, setWorkspaceResolveAttempted])

  return {
    isAuthenticated,
    isLoading,
    email,
    workspaceId,
    signInError,
    magicLinkSent,
    workspaceResolveError,
    retryWorkspaceResolve,
    signIn,
    verifyOtpCode,
    signOut,
    clearError,
  }
}
